/**
 * A CARD PAGE FROM A COLLECTION OF ANY SIZE (card-scale, 21 October 2026).
 *
 * Every collection today is held whole on the server and its pages carry a
 * total. A very large one is read from its collection index, which does not
 * count: its pages carry `hasMore` instead. The screens keep their one notion
 * of how many pages there are ("at least one more while more follow") and
 * name the page without a total.
 *
 * The index can be not ready to answer: being built (503
 * `collection_index_building`, normally ahead of need, but a language first
 * read or a new index version builds then), or a page further than one
 * request reaches (202 `collection_page_in_progress`: a rare search or a deep
 * jump, whose progress the server keeps). The screen's request waits that out
 * for as long as the screen still wants the page, and says so after a moment.
 */

const INDEX_WAIT_MS = 30 * 60 * 1000;
const WAITING_GRACE_MS = 1500;
const BUILD_POLL_MAX_MS = 10_000;

/** @param {any} answer */
export function normalizePageAnswer(answer) {
    if (!answer || typeof answer !== 'object' || answer.totalPages !== null) return answer;
    const page = Number(answer.page) || 1;
    const items = Array.isArray(answer.items) ? answer.items : [];
    // A first page with nothing on it and nothing after is a count after
    // all: none (the screens' "no cards match" state).
    const none = page === 1 && items.length === 0 && !answer.hasMore;
    return {
        ...answer,
        ...(none ? { totalItems: 0 } : {}),
        totalPages: answer.hasMore ? page + 1 : page,
        totalsUnknown: true,
    };
}

function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(signal.reason || new DOMException('Aborted', 'AbortError'));
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal?.addEventListener?.('abort', () => {
            clearTimeout(timer);
            reject(signal.reason || new DOMException('Aborted', 'AbortError'));
        }, { once: true });
    });
}

/**
 * fetch(), waiting out an index that cannot answer yet, then answering as
 * fetch does. A prefetch passes `maxWaitMs: 0` and takes the first answer.
 * `wanted` ends the wait once the screen no longer wants the page;
 * `onWaiting` hears 'preparing' or 'searching' once the wait has outlasted
 * WAITING_GRACE_MS, and null when it ends.
 * @param {string} url
 * @param {RequestInit} [init]
 * @param {{ maxWaitMs?: number, wanted?: () => boolean, onWaiting?: ((state: 'preparing' | 'searching' | null) => void) | null }} [options]
 */
export async function fetchPageAnswer(url, init = {}, { maxWaitMs = INDEX_WAIT_MS, wanted = () => true, onWaiting = null } = {}) {
    const started = Date.now();
    /** @type {'preparing' | 'searching' | null} */
    let said = null;
    let builds = 0;
    /** @param {'preparing' | 'searching'} state */
    const say = (state) => {
        if (said === state) return;
        said = state;
        onWaiting?.(state);
    };
    try {
        for (;;) {
            const response = await fetch(url, init);
            if (response.status !== 503 && response.status !== 202) return response;
            if (Date.now() - started >= maxWaitMs || !wanted()) return response;
            let delay = 0;
            /** @type {'preparing' | 'searching'} */
            let state = 'searching';
            if (response.status === 503) {
                const body = await response.clone().json().catch(() => null);
                if (body?.error !== 'collection_index_building') return response;
                // From the server's hint, backing off to a poll every ten seconds.
                const hinted = Math.min(Math.max(Number(body.retryAfterMs) || 2000, 500), 5000);
                delay = Math.min(BUILD_POLL_MAX_MS, Math.round(hinted * 1.5 ** Math.min(builds, 4)));
                builds += 1;
                state = 'preparing';
            }
            if (Date.now() - started + delay >= WAITING_GRACE_MS) say(state);
            if (delay) await sleep(delay, init.signal);
            if (!wanted()) return response;
        }
    } finally {
        if (said) onWaiting?.(null);
    }
}

/**
 * The page label: "Page 3 of 9", or "Page 3" when the collection's pages are
 * not counted.
 * @param {(key: string, fallback: string, params?: any) => string} uiText
 * @param {{ page: number, totalPages: number, totalsUnknown?: boolean }} pagination
 */
export function pageLabelFor(uiText, pagination) {
    const page = Number(pagination?.page) || 1;
    if (pagination?.totalsUnknown) return uiText('common.page', `Page ${page}`, { page });
    const total = Number(pagination?.totalPages) || 1;
    return uiText('common.pageOf', `Page ${page} of ${total}`, { page, total });
}
